import { Cell, Edge, Graph, Node } from "@antv/x6"
import { LineType, PortTypes } from "../constant"
import { CellDataWithId, FlowsheetContent, LineJumpData, LineJumpRecord, LineRecord, NodeRecord, NodeShapes, NodeState, SetOperation, SheetContent, UnitTagState } from "./modal"
import { addVirtualPoints } from "./lineService"
import { v4 as uuidv4 } from "uuid";
import { getGraphSvg, getLineCenter, isLineValid } from "../utils"
import { NozzleType } from "@/types/common"
import { log } from "@/common/log"
import { ISheetData } from "@/store/flowsheet/flowsheet.slice"


export function edgeToLineRecord(edge: Cell.Properties): LineRecord {
  const { id, vertices = [], source, target, zIndex, attrs } = edge
  const isHide = attrs?.path?.stroke === 'transparent'

  return {
    id: id!,
    tag: edge.data.tag,
    type: edge.data.type,
    vertices,
    source,
    target,
    zIndex: zIndex || 0,
    showTag: edge.data.showTag,
    tagOption: edge.data.tagOption,
    streamId: edge.data.streamId,
    hide: isHide,
  }
}

function mergeImages(sheets: SheetContent[]): {
  nodeImages: {[key: string]: string}, nozzleImages: { [key: string]: string }
} {
  const nodeImages = {}
  const nozzleImages = {}
  const nodes = sheets.flatMap(item => item.nodes)
  const imageNodes = nodes.filter(item => !!item.image)
  imageNodes.forEach((node) => {
    const {nozzles} = node
    const { equipment } = node
    nodeImages[equipment.id] = encodeURIComponent(node.image);
    nozzles.filter(item => item.group === PortTypes.SvgNozzlePort).forEach(item => {
      const {shapeId, image} = item
      nozzleImages[shapeId!] = encodeURIComponent(image!)
    })
  })
  return {nodeImages: nodeImages, nozzleImages: nozzleImages,}
}


export function cellToNodeState(cell: Cell.Properties | Node): NodeState {
  if (cell.isNode && cell.isNode()) {
    cell = cell.toJSON();
  }
  const { id, ports, position, size, angle, data } = cell
  const nodeState: NodeState = data;
  const nozzles = ports.items
  .filter(item => item.data.type === NozzleType.FlowNozzle)
  .map(port => {
    const {id, group, data} = port
    return {...data, id, group}
  })

  const customNozzles = ports.items
  .filter(item => item.data.type === NozzleType.CustomFlowNozzle)
  .map(port => {
    const {id, group, data} = port
    return {...data, id, group}
  });

  const lineNozzles = ports.items
  .filter(item => item.group === PortTypes.LinePort)
  .map(port => {
    const {id, group, data} = port
    return {...data, id, group}
  });

  return {
    ...nodeState,
    id: id!,
    angle,
    position,
    size,
    nozzles: nozzles,
    customNozzles: customNozzles,
    lineNozzles: lineNozzles
  }
}


export function cellToLineJumpRecord(cell: Cell.Properties): LineJumpRecord {
  const {id, size, position, ports, data, angle = 0, zIndex} = cell as CellDataWithId;
  const nozzles = ports.items.map(item => {
    return {
      id: item.id,
      position: item.args
    }
  })
  const jumpData = data as LineJumpData

  return {
    id,
    size,
    position,
    angle,
    ports: nozzles,
    data: jumpData,
    zIndex
  }
}

export function nodeStateToNodeRecord(node: NodeState): NodeRecord {
  return node as NodeRecord
}

export function isLine(cell: Cell) {
  return cell.shape === 'edge'
}

function unitTagToTagOptions(tagNode:Cell.Properties): UnitTagState {
  const { size, data, angle } = tagNode
  const {unit, style, offset, text} = data

  return {
    unit,
    size,
    style,
    offset,
    angle,
    text
  }
}


export async function cellsToSheetData(data: { cells: Cell.Properties[] }, graph: Graph): Promise<ISheetData> {
  const {cells} = data
  const nodes: NodeRecord[] = [];
  const streams: LineRecord[] = [];
  const polylines: Cell.Properties[] = [];
  const overridedNodes: Cell.Properties[] = [];

  const tags: Cell.Properties[] = []
  const connections: Cell.Properties[] = []
  const lineJumps: Cell.Properties[] = []
  cells.forEach(item => {
    if (item.shape === 'edge' && item.data.type === LineType.FlowLine) {
      const line = edgeToLineRecord(item)
      streams.push(line)
      return
    }
    if (item.shape === 'edge' && item.data.type === LineType.Line) {
      if (isLineValid(item)) {
        if (item.attrs?.path?.pointerEvents) {
          delete item.attrs?.path?.pointerEvents;
        }
        polylines.push(item)
      } else {
        log('Line is not valid, the data is:', item)
      }
      return
    }
    if (item.shape === NodeShapes.UnitTag) {
      tags.push(item)
      return
    }
    if (item.shape === NodeShapes.SvgNode) {
      const node = nodeStateToNodeRecord(cellToNodeState(item))
      nodes.push(node)
    }
    if (item.shape === NodeShapes.SvgStream) {
      connections.push(item)
    }
    if (item.shape === NodeShapes.LineJump) {
      lineJumps.push(cellToLineJumpRecord(item))
    }
    if (item.shape === NodeShapes.TextNode && item.data.override) {
      overridedNodes.push(item)
    }
    if (item.shape === NodeShapes.ImageNode && item.data.override) {
      overridedNodes.push(item)
    }
  })
  addVirtualPoints(streams, graph)

  tags.forEach(tag => {
    const targetNode = nodes.find(item => item.id === tag.data.unit)
    if (targetNode) {
      const targetPosition = targetNode.position!
      const tagPosition = tag.position
      const offset = {
        x: tagPosition.x - targetPosition.x,
        y: tagPosition.y - targetPosition.y,
      }
      tag.data.offset = offset;
      targetNode.tagOption = unitTagToTagOptions(tag)
    } else {
      const targetLine = streams.find(item => item.id === tag.data.unit)
      if (targetLine) {
        const edge = graph.getCellById(targetLine.id) as Edge
        const center = getLineCenter(edge, graph)
        if (center) {
          const tagOption = unitTagToTagOptions(tag)
          targetLine.tagOption = {...tagOption, offset: {...tagOption.offset, cx: center.x, cy: center.y}}
        }
      }
    }
  })
  
  return {
    nodes,
    lines: streams,
    polylines,
    connections,
    jumps: lineJumps,
    overridedNodes
  }
}

export function getEmptySheetContent(name?: string) {
  return {
    id: uuidv4(),
    name: name || 'Sheet 1',
    nodes: [],
    lines: [],
    connections: [],
  }
}

export function getEmptyFlowSheetContent(): FlowsheetContent {
  return {
    nodeImages: {},
    nozzleImages: { },
    sheets: [getEmptySheetContent()],
    tagState: {},
    setOperations: [],
    spreadsheets: [],
  }
}

export function cacheImages(content: FlowsheetContent): FlowsheetContent {
  const {sheets} = content
  const {nodeImages, nozzleImages} = mergeImages(sheets)
  const newSheets = sheets.map(sheet => {
    const nodes = [...sheet.nodes]
    const newNodes = nodes.map(node => {
      const nozzles = node.nozzles.map(nozzle => {
        return {...nozzle, image: ''}
      })
      return {...node, nozzles, image: ''}
    })

    return {...sheet, nodes: newNodes}
  })
  
  return {
    ...content,
    nozzleImages,
    nodeImages,
    sheets: newSheets
  }
}